/**
 * Patient Chart wire types — Allergies, Conditions/Problems, Procedures,
 * Immunizations, Flags, History, Medications, Documents, Notes, Labs.
 *
 * Mirror Flutter `patient_chart_data_source_impl.dart` shapes. Endpoints
 * live on the **Patients** service base (`/api/Patients/*`).
 *
 * Naming convention: TS types use FHIR-style names (`PatientAllergy`,
 * not Flutter's plural `PatientAllergies` — which is the LIST class, not
 * a single record).
 */

import type { CodeWordCase } from './patient-api';
// PROD-1769: `PatientDocument` below carries the document's CPT/radiology code
// and lab rows. Reusing the documents-module shapes rather than redeclaring them
// keeps the adapter's explicit field mapping type-checkable in both directions.
import type {
  PatientDocumentData,
  PatientDocumentLab,
} from '@/documents/types/document';

// ─── Allergies (PROD-736 PR A) ───────────────────────────────────────────────

/**
 * Reaction sub-resource on an allergy. Flutter `Reaction` class.
 *
 * `substance` ships as a Coding; `onset` and `severity` are bare strings
 * (NOT Codings) — Flutter `n/a` defaults imply the wire accepts the
 * literal "n/a" placeholder, but in React we ship `null` instead.
 */
export interface AllergyReaction {
  substance?: CodeWordCase | null;
  onset?: string | null;
  severity?: string | null;
}

/** Single patient allergy record (FHIR `AllergyIntolerance` R4 alias). */
export interface PatientAllergy {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  clinicalStatus?: string | null;
  type?: string | null;
  /** Allergy category (Food / Medication / Environmental / Biologic). The
   *  Coding's `code` carries `"DA"` for drug allergies; `isNonDrug` is
   *  computed locally from this. */
  category?: CodeWordCase | null;
  criticality?: CodeWordCase | null;
  /** The allergen itself — `code.display` is the human-readable name. */
  code?: CodeWordCase | null;
  /** Patient id (subject reference). */
  subject?: string | null;
  dataAbsentReason?: string | null;
  /** Encounter the allergy was first recorded in. */
  encounter?: string | null;
  /** ISO 8601 when the patient first noticed the reaction. */
  onset?: string | null;
  onSetDisplay?: string | null;
  /** ISO 8601 when this record was created. */
  recordedDate?: string | null;
  recorder?: string | null;
  asserter?: string | null;
  note?: string | null;
  reaction?: AllergyReaction | null;
  /** Tombstone flag — `true` for soft-deleted records. The wire always
   *  ships this field (verified across every record returned on 2026-05-15),
   *  so the type is REQUIRED. If a future backend change ever drops the
   *  field, TS will catch it at the consumer instead of silently leaking
   *  deleted records through `!isDeleted` truthy-coerce filters. Samer
   *  PR #157 review 2026-05-15. */
  isDeleted: boolean;
  /** Audit timestamp; backend assigns on create. */
  createdDate?: string | null;
  /** Recording provider details — GET ships the full shape (with
   *  speciality / timeZone / organizationId / roleName) and the
   *  UPDATE wire requires the same payload to round-trip. Most read
   *  consumers only touch `id` / `name` / `photo`, hence the loose
   *  type. */
  performerDetails?: Partial<PerformerDetailsFull> | null;
  /** Specialty of the recording provider. */
  speciality?: CodeWordCase | null;
}

/** Search result row from `/api/Master/AllergyCode?searchQuery=`.
 *  Mirrors Flutter `CodeCategoryData` (`allergy_data.dart`). Each row is
 *  a CodeWordCase EXTENDED with an optional nested `category` Coding
 *  (food/environmental/etc.). It is also the picker's in-memory shape for
 *  a DRUG allergen: a medication picked from
 *  `SearchValueSets2?SearchFor=Medications` is carried with
 *  `category = DRUG_ALLERGY_CATEGORY` (`{ code: "DA", system: "NAB042" }`).
 *
 *  This is a PICKER shape, not a wire shape. The nested `category` must
 *  never be sent under `code`: the backend's CreatePatientAllergyRequest
 *  reads only the TOP-LEVEL `category` (its `Code` class has no category
 *  field), so a nested one is silently dropped (PROD-1998). */
export interface AllergyCodeOption {
  code: string | null;
  display: string | null;
  system?: string | null;
  description?: string | null;
  shortName?: string | null;
  /** Allergy category Coding nested under the allergen itself.
   *  E.g. `{ code: "FA", display: "Food allergy", system: "NAB042" }`. */
  category?: CodeWordCase | null;
}

/**
 * POST `/api/Patients/CreateAllergy` request body (curl-verified
 * 2026-05-19 by Andrew). Mirrors Flutter `PatientAllergies.toJson()`.
 *
 * Wire shape notes:
 * - `identifier: null` on create (curl uses literal null, not omission).
 * - `clinicalStatus: "Active"` and `type: "allergy"` are literal
 *    constants for the chart-tab Add flow.
 * - `category` is the TOP-LEVEL Coding — the only place the backend
 *    reads it (Scrips.Patient `CreatePatientAllergyRequest.category`; the
 *    `code` Coding has no category field). Drug allergy →
 *    `{ code: "DA", display: "Drug allergy", system: "NAB042" }` (Flutter
 *    `converter_utils.dart`); non-drug → the picked AllergyCode row's own
 *    category, or `null` when the row carries none. An earlier version of
 *    this doc said create ships `category: null` with the Coding nested
 *    under `code.category` — that copied a Flutter chart-tab bug and the
 *    backend dropped the category, storing null (PROD-1998).
 * - `code` is a plain Coding — no nested category.
 * - `dataAbsentReason: "n/a"` and `reaction.onset`/`reaction.severity`
 *    as literal `"n/a"` strings — verified by curl, not symbolic null.
 * - `onset`: ISO 8601 WITHOUT the trailing `Z` (`.NET` model binder
 *    rejects the UTC marker per the project's date-format convention).
 *    Format: `YYYY-MM-DDTHH:mm:ss.sss`.
 * - `onSetDisplay`: human-typed form ("2024", "May 2024", or the
 *    formatted date string). Carries the user's free-text intent;
 *    `onset` carries the resolved ISO.
 * - `recorder`: GUID of the recording user (`useAuthStore.user.userId`).
 * - `recordedDate: null`, `createdDate: null`, `performerDetails: null`,
 *    `speciality: null`, `asserter: null` — server fills these on create.
 * - `isDeleted: false` on create; soft-delete via PUT with
 *    `isDeleted: true`.
 */
export interface CreateAllergyBody {
  identifier: null;
  clinicalStatus: 'Active';
  type: 'allergy';
  /** Top-level allergy category Coding (NAB042). `null` only when the
   *  picked non-drug allergen has no category. */
  category: CodeWordCase | null;
  /** Severity Coding (UI label "Severity" maps to wire `criticality`).
   *  Sourced from `GET /api/Master/AllergenSeverity`. */
  criticality: CodeWordCase | null;
  /** The allergen itself — plain Coding, no nested category. Non-drug
   *  allergens come from `GET /api/Master/AllergyCode?searchQuery=`; drug
   *  allergens from `GET /api/Master/SearchValueSets2?SearchFor=Medications`
   *  (the org's medication catalogue), saved verbatim. */
  code: CodeWordCase | null;
  subject: string;
  /** Literal "n/a" string — curl-verified. */
  dataAbsentReason: 'n/a';
  encounter: string | null;
  /** ISO 8601 without trailing `Z` (.NET model binder convention). */
  onset: string | null;
  /** User's free-text form (e.g. "2024" when only the year was given). */
  onSetDisplay: string | null;
  recordedDate: null;
  /** GUID of the recording user. */
  recorder: string;
  asserter: null;
  /** Description field. */
  note: string | null;
  reaction: {
    /** Reaction Coding — sourced from `GET /api/Master/AllergenReaction`. */
    substance: CodeWordCase | null;
    /** Literal "n/a" — curl-verified, not user-editable. */
    onset: 'n/a';
    /** Literal "n/a" — curl-verified, not user-editable. */
    severity: 'n/a';
  };
  isDeleted: false;
  performerDetails: null;
  createdDate: null;
  speciality: null;
}

/**
 * Full performer details as they come back from `GetPatientAllergyDetails`
 * and as they need to round-trip through the UPDATE wire body. The
 * read-side `PatientAllergy.performerDetails` is the trimmed shape
 * (id/name/photo only); this richer shape is what `PUT /allergy/{id}`
 * actually ships. We carry it as opaque on the body to avoid coupling
 * the React layer to the full provider schema — the server populated
 * these fields on GET and accepts them back verbatim on PUT.
 */
export interface PerformerDetailsFull {
  id: string | null;
  name: string | null;
  photo: string | null;
  speciality: {
    specialitySkillId: string | null;
    code: {
      code: string | null;
      displayName: string | null;
      system: string | null;
      description: string | null;
      shortName: string | null;
    } | null;
  } | null;
  timeZone: string | null;
  organizationId: string | null;
  roleName: string | null;
}

/**
 * PUT `/api/Patients/allergy/{id}` request body (curl-verified
 * 2026-05-19 by Andrew — both EDIT and DELETE flows ship through this
 * endpoint, only `isDeleted` differs).
 *
 * **Wire-shape differences from `CreateAllergyBody`** (important):
 *
 * - `identifier` is the existing GUID (CREATE ships null).
 * - `category` is the TOP-LEVEL Coding — same as CREATE. The typeahead
 *   search returns the category nested in the allergen row; extract it
 *   to top-level on submit.
 * - `code` is a plain `CodeWordCase` — no nested category (same as CREATE).
 * - `recordedDate`, `asserter`, `createdDate`, `performerDetails`
 *   are populated from the original record on round-trip. Don't
 *   overwrite `recorder` with the current user — recorder stays the
 *   ORIGINAL recording user (curl-verified: both Edit and Delete
 *   curls keep recorder as the original `035819e5-...` id even
 *   though the editing user is `99d04d2a-...`).
 * - `isDeleted: false` for EDIT, `isDeleted: true` for soft-DELETE.
 *   Both use the same endpoint.
 */
export interface UpdateAllergyBody {
  identifier: string;
  clinicalStatus: 'Active';
  type: 'allergy';
  /** Top-level category Coding on UPDATE (NOT nested under code). */
  category: CodeWordCase | null;
  criticality: CodeWordCase | null;
  /** Plain Coding — no nested category on UPDATE. */
  code: CodeWordCase | null;
  subject: string;
  dataAbsentReason: 'n/a';
  encounter: string | null;
  onset: string | null;
  onSetDisplay: string | null;
  /** Original-record creation timestamp (preserved on edit). */
  recordedDate: string | null;
  /** Original recording user (NOT overwritten with the editing user). */
  recorder: string;
  /** The asserter identity, or null when unknown. Never filled from `recorder` (ruling 1 Nov 2026). */
  asserter: string | null;
  note: string | null;
  reaction: {
    substance: CodeWordCase | null;
    onset: 'n/a';
    severity: 'n/a';
  };
  /** `false` for EDIT, `true` for soft-DELETE. */
  isDeleted: boolean;
  performerDetails: PerformerDetailsFull | null;
  createdDate: string | null;
  speciality: CodeWordCase | null;
}

// ─── Problems / Conditions (PROD-736 PR B) ───────────────────────────────────

/** Body-site sub-resource on a condition. Mirrors Flutter `BodySiteCond`. */
export interface ConditionBodySite {
  code?: string | null;
  display?: string | null;
  system?: string | null;
}

/**
 * Empty `bodySite` sentinel for the CreateCondition wire body.
 *
 * The backend rejects `bodySite: null` — it expects the three-key
 * object shape with all fields present-and-null. Curl-verified
 * 2026-05-19. This constant is the single source of truth so callers
 * can't typo the sentinel or forget a key. Samer PR #225 review.
 *
 * On UPDATE, the existing record's bodySite is round-tripped through
 * `emptyBodySite()` (in `add-problem-editor.tsx`) which coalesces
 * missing fields against this constant.
 */
export const EMPTY_BODY_SITE: ConditionBodySite = Object.freeze({
  system: null,
  code: null,
  display: null,
}) as ConditionBodySite;

/**
 * Active / Resolved status keys for `/GetConditionStatusDetails?status=`.
 * Literal strings — Flutter `scrips_core/utils/utils.dart:534-535`:
 *   const conditionStatusActive = "Active";
 *   const conditionStatusResolved = "Resolved";
 */
export const CONDITION_STATUS = {
  active: 'Active',
  resolved: 'Resolved',
} as const;
export type ConditionStatus =
  (typeof CONDITION_STATUS)[keyof typeof CONDITION_STATUS];

/**
 * Single patient condition record (FHIR `Condition` R4 alias).
 *
 * `clinicalStatus` is the bucket key — Active rows render in the upper
 * section of the Problems tab; Resolved rows in the lower section.
 *
 * `code.code === 'FreeText'` flags a free-text condition the user typed
 * in directly (not from the master code list). Flutter renders these
 * with `code.text` (the typed string) instead of looking up a SNOMED
 * display.
 */
export interface PatientCondition {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  /** "Active" | "Resolved" | other server-defined values. */
  clinicalStatus?: string | null;
  code?: ConditionCode | null;
  bodySite?: ConditionBodySite | null;
  subject?: string | null;
  dataAbsentReason?: string | null;
  encounter?: string | null;
  asserter?: string | null;
  /** ISO 8601 — when the problem started. */
  onsetDateTime?: string | null;
  onsetDisplayDateTime?: string | null;
  /** ISO 8601 — when the problem resolved. Null for active. */
  endDate?: string | null;
  endDisplayDate?: string | null;
  note?: string | null;
  /** Severity is a bare STRING on Conditions ("Mild" / "Moderate" /
   *  "Severe" / "n/a") — NOT a Coding like Allergies' `criticality`.
   *  Curl-verified 2026-05-19. */
  severity?: string | null;
  /** Tombstone flag — required (same pattern as `PatientAllergy.isDeleted`
   *  per Samer PR #157 review 2026-05-15). */
  isDeleted: boolean;
  /** GET ships the full performer shape; UPDATE round-trip preserves it.
   *  Widened from the slim {id,name,photo} shape after PROD-827 Allergies
   *  Edit/Delete shipped — same rationale, see PerformerDetailsFull. */
  performerDetails?: Partial<PerformerDetailsFull> | null;
  createdDate?: string | null;
  speciality?: CodeWordCase | null;
}

/**
 * Condition code shape — `CodeWordCase` extended with a **PascalCase**
 * `Definition` field. The master endpoint `/Master/DiagnosisCodes`
 * returns the full long-form definition under `Definition` (capital D!)
 * alongside the short-form `display`. Curl-verified 2026-05-19.
 *
 * On round-trip (GET → UPDATE), `Definition` is `null` because the
 * GET response doesn't include it — but the field is preserved on the
 * wire even as null. Master-search picks fresh from the typeahead
 * have it populated.
 */
export interface ConditionCode {
  code?: string | null;
  /** **PascalCase D** — wire literal. Long-form description from the
   *  ICD-10 master catalog. Null on GET round-trip. */
  Definition?: string | null;
  display?: string | null;
  system?: string | null;
  description?: string | null;
  shortName?: string | null;
}

/**
 * POST `/api/Patients/CreateCondition` request body (curl-verified
 * 2026-05-19 by Andrew). Mirrors Flutter `createCondition()`.
 *
 * **Wire-shape gotchas:**
 * - `code.Definition` PascalCase (capital D!) — wire literal.
 * - `bodySite` is an empty Coding `{system:null, code:null, display:null}`
 *   on Create, NOT `null`. Backend rejects null.
 * - `severity` is a bare STRING ("Mild" / "Moderate" / "Severe" / "n/a"),
 *   NOT a Coding. Unlike Allergies which uses a Coding under
 *   `criticality`.
 * - `asserter` is the CURRENT user's `userId` GUID on Create. Different
 *   from Allergies which uses `recorder` for the same role.
 * - `performerDetails` on Create is the editing user's slim record:
 *   `id: null`, `name` populated, `speciality.specialitySkillId` ships
 *   the all-zero GUID sentinel `"00000000-0000-0000-0000-000000000000"`.
 * - `createdDate` is CLIENT-SET on Create (`new Date().toISOString()`
 *   without `Z`). Curl observation suggests the **backend overwrites
 *   this on persist** — UPDATE response's `createdDate` was preserved
 *   from the prior server-known value, not from the curl payload's
 *   client-set time. We ship the field because the curl includes it
 *   verbatim (backend rejects missing keys with 400), but treat its
 *   stored value as advisory-only.
 *
 *   ⚠️ **PHI integrity caveat** (Samer PR #225 review): if a
 *   clinician's device clock is skewed, the wire body's `createdDate`
 *   carries that skew. As long as the backend overwrites the value
 *   (assumption above), this is harmless. If a future backend change
 *   starts persisting the client value, this becomes an issue — flag
 *   if you observe drift between the shipped value and the GET-side
 *   `createdDate`.
 * - `speciality` top-level is `null` on Create.
 */
export interface CreateConditionBody {
  identifier: null;
  clinicalStatus: 'Active';
  code: ConditionCode | null;
  bodySite: ConditionBodySite;
  subject: string;
  dataAbsentReason: 'n/a';
  encounter: string | null;
  /** Current user's userId GUID. */
  asserter: string;
  endDisplayDate: null;
  onsetDisplayDateTime: string | null;
  onsetDateTime: string | null;
  endDate: null;
  note: string;
  /** Plain string — "Mild" / "Moderate" / "Severe" / "n/a". */
  severity: string;
  isDeleted: false;
  performerDetails: ConditionPerformerOnCreate;
  /** Client-set ISO timestamp (no trailing Z). See type docblock —
   *  assumed advisory-only; backend overwrites. */
  createdDate: string;
  speciality: null;
}

/**
 * The slim performer shape Conditions Create ships — id is null,
 * name is the editing user's display name, and speciality uses the
 * all-zero GUID sentinel + null code. Distinct from
 * `PerformerDetailsFull` which is the UPDATE-side round-trip shape.
 */
export interface ConditionPerformerOnCreate {
  id: null;
  name: string | null;
  photo: null;
  speciality: {
    /** All-zero GUID sentinel: "00000000-0000-0000-0000-000000000000". */
    specialitySkillId: '00000000-0000-0000-0000-000000000000';
    code: null;
  };
  timeZone: null;
  organizationId: null;
  roleName: null;
}

/**
 * PUT `/api/Patients/condition/{id}` request body (curl-verified
 * 2026-05-19 by Andrew — covers Edit, Resolve, and soft-Delete; only
 * `clinicalStatus` / `endDate` / `endDisplayDate` / `isDeleted` differ).
 *
 * **Round-trip rules** (different from Allergies in two places):
 * - `clinicalStatus`: `"Active"` for Edit/Delete, `"Resolved"` for the
 *   Resolve flow.
 * - `endDate` + `endDisplayDate`: `null` on Edit, populated when
 *   Resolving (ISO + "DD/MM/YYYY" display).
 * - `speciality` top-level IS populated from the original record —
 *   unlike Allergies where Flutter ships top-level `speciality: null`.
 * - `bodySite` round-trips from the original record (can be the
 *   empty-Coding shape or a populated body site).
 * - `asserter`, `performerDetails`, `createdDate` preserve original
 *   values.
 */
export interface UpdateConditionBody {
  identifier: string;
  clinicalStatus: 'Active' | 'Resolved';
  code: ConditionCode | null;
  bodySite: ConditionBodySite;
  subject: string;
  dataAbsentReason: 'n/a';
  encounter: string | null;
  asserter: string | null;
  /** "DD/MM/YYYY" — populated when Resolving, null on Edit. */
  endDisplayDate: string | null;
  onsetDisplayDateTime: string | null;
  onsetDateTime: string | null;
  /** ISO (no trailing Z) — populated when Resolving, null on Edit. */
  endDate: string | null;
  note: string;
  severity: string;
  /** `false` for Edit/Resolve, `true` for soft-Delete. */
  isDeleted: boolean;
  performerDetails: PerformerDetailsFull | null;
  createdDate: string | null;
  /** Populated from original record on round-trip (NOT null like
   *  Allergies). */
  speciality: CodeWordCase | null;
}

// ─── Procedures (PROD-736 PR C) ──────────────────────────────────────────────

/**
 * Procedure code shape — **uses `displayName`, NOT `display`** (the Flutter
 * class is literally called `CodeDisplayName`). Distinct from every other
 * chart resource's `code` field, which uses `display`. Verified against
 * the live wire 2026-05-15.
 *
 *   "code": { "system": null, "code": "12057", "displayName": "..." }
 */
export interface CodeDisplayName {
  code?: string | null;
  /** `displayName` PascalCase — NOT `display`. The Flutter class is
   *  literally called `CodeDisplayName`. Used by Procedures'
   *  `code` field and the nested `performerDetails.speciality.code`. */
  displayName?: string | null;
  system?: string | null;
  /** Long-form description from the master endpoint. Procedures
   *  curl-verified 2026-05-20 ships this field. */
  description?: string | null;
  shortName?: string | null;
}

/**
 * Single patient procedure record (FHIR `Procedure` R4 alias).
 *
 * Flutter ref: `patient_procedures_model.dart`. `code` is the procedure
 * itself (CPT / SNOMED) — note the `displayName` field divergence from
 * other chart resources.
 *
 * `outcome` is a short free-text string ("success", "improved", "n/a",
 * etc. — backend not strictly enumerated).
 *
 * `reason` is FHIR's "why was this done" — backend ships it as a
 * BodySite-shaped object (Flutter reuses the BodySite class to
 * deserialise; it's just a Coding wrapper either way).
 */
export interface PatientProcedure {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  partOf?: string | null;
  clinicalStatus?: string | null;
  category?: string | null;
  /** The procedure itself (CPT / SNOMED). Uses `displayName`, not
   *  `display` — see CodeDisplayName for the wire-shape rationale. */
  code?: CodeDisplayName | null;
  /** Where on the body the procedure was performed. Uses regular
   *  `display` field (NOT displayName — the wire is inconsistent). */
  bodySite?: ConditionBodySite | null;
  subject?: string | null;
  recorder?: string | null;
  encounter?: string | null;
  performer?: string | null;
  asserter?: string | null;
  /** Reference to a Condition / DiagnosticReport / etc. */
  reasonReference?: string | null;
  /** Why the procedure was done — backend ships a BodySite-shaped object. */
  reason?: ConditionBodySite | null;
  outcome?: string | null;
  /** Backend typo: `compilation` not `complication`. Preserve verbatim. */
  compilation?: string | null;
  /** ISO 8601 when the procedure was performed. */
  onsetDateTime?: string | null;
  onsetDateTimeDisplay?: string | null;
  note?: string | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  /** GET ships the full performer shape; UPDATE round-trip preserves
   *  it. Widened to `Partial<PerformerDetailsFull>` after PROD-827
   *  Allergies Edit/Delete shipped — same rationale, see
   *  PerformerDetailsFull. */
  performerDetails?: Partial<PerformerDetailsFull> | null;
  createdDate?: string | null;
  speciality?: CodeWordCase | null;
}

/**
 * POST `/api/Patients/CreateProcedure` request body (curl-verified
 * 2026-05-20 by Andrew).
 *
 * **Procedures-specific wire-shape gotchas — DIFFER from Conditions:**
 * - `code` uses **`displayName`** (NOT `display`). Same `CodeDisplayName`
 *   shape as the master endpoint's `/Master/ProcedureCodesList` rows.
 * - `dataAbsentReason: ""` on Create — empty STRING, NOT `"n/a"` like
 *   Conditions / Allergies. Update uses `"n/a"` instead — wire is
 *   intentionally inconsistent (curl-verified both sides).
 * - **BOTH `recorder` AND `asserter`** populated on Create with the
 *   current user's GUID. Conditions only had `asserter`; Allergies
 *   only had `recorder`. Procedures has both as separate fields.
 * - `category` is a **STRING** (not a Coding). Examples: `"food"`,
 *   `null` on Create. Update can ship the same string back.
 * - `bodySite` + `reason` are populatable Codings — empty-Coding
 *   sentinel on Create (`{system:null, code:null, display:null}`).
 * - `outcome` / `compilation` are bare strings (defaults `"n/a"`).
 *   **`compilation` is a BACKEND TYPO** of `complication` — wire
 *   literal; preserve verbatim, do NOT "fix" the spelling.
 * - `createdDate: null` on Create — Conditions client-sets it,
 *   Procedures lets the server fill it. Different convention.
 * - `performerDetails: null` on Create — Conditions ships the slim
 *   sentinel performer; Procedures sends null. The server fills the
 *   full shape from the user identity headers.
 * - `partOf: null`, `performer: null`, `reasonReference: null`,
 *   `speciality: null` — all literal null on Create.
 * - `onsetDateTimeDisplay`: dynamic-date free-text ("04/2026" /
 *   "2024" / "1Y" / etc.) — partner to `onsetDateTime` (ISO no-Z).
 */
export interface CreateProcedureBody {
  onsetDateTimeDisplay: string | null;
  identifier: null;
  partOf: null;
  clinicalStatus: 'Active';
  /** Plain string label (e.g. "food") OR null. NOT a Coding. */
  category: string | null;
  code: CodeDisplayName | null;
  bodySite: ConditionBodySite;
  /** Empty string `""` on Create — Procedures-specific wire quirk.
   *  Update ships `"n/a"`. Don't try to unify; backend differentiates. */
  dataAbsentReason: '';
  subject: string;
  /** Current user's userId on Create. */
  recorder: string;
  encounter: string | null;
  performer: null;
  /** Current user's userId on Create (same as recorder). */
  asserter: string;
  reasonReference: null;
  reason: ConditionBodySite;
  outcome: string;
  /** Backend typo `compilation` (should be `complication`) — preserve
   *  verbatim. **Tracked for cleanup: PROD-842** (backend wire-literal
   *  cleanup ticket, filed 2026-05-20 after Samer review on PR #245).
   *  Cannot rename unilaterally — Flutter PM/PA still ships the typo
   *  and the .NET service reads the literal field name. */
  compilation: string;
  onsetDateTime: string | null;
  note: string;
  isDeleted: false;
  performerDetails: null;
  /** Server-assigned on Create — ship null. */
  createdDate: null;
  speciality: null;
}

/**
 * PUT `/api/Patients/procedure/{id}` request body (curl-verified
 * 2026-05-20 by Andrew — covers Edit and soft-Delete; only `isDeleted`
 * differs).
 *
 * **Round-trip rules vs Conditions:**
 * - `dataAbsentReason: "n/a"` on Update (Create shipped `""` —
 *   intentionally inconsistent per backend).
 * - `recorder: null` on Update — the recording-user GUID is NOT
 *   round-tripped (unlike Conditions which preserves `asserter` or
 *   Allergies which preserves `recorder`). The asserter field carries
 *   the original recording user instead.
 * - `asserter` preserved from original record.
 * - `category` is a string (or null) — round-tripped from GET.
 * - `bodySite` + `reason` round-trip the original Codings.
 * - `performerDetails` round-trips the full shape.
 * - `createdDate` preserved from original record.
 * - Top-level `speciality: null` (same as Conditions).
 * - `compilation` typo preserved on the wire.
 */
export interface UpdateProcedureBody {
  onsetDateTimeDisplay: string | null;
  identifier: string;
  partOf: null;
  clinicalStatus: 'Active' | 'Resolved';
  category: string | null;
  code: CodeDisplayName | null;
  bodySite: ConditionBodySite;
  dataAbsentReason: 'n/a';
  subject: string;
  /** NULL on Update — recording-user GUID isn't round-tripped here
   *  (different from Conditions / Allergies). The asserter carries
   *  the recording-user identity instead. */
  recorder: null;
  encounter: string | null;
  performer: null;
  asserter: string;
  reasonReference: null;
  reason: ConditionBodySite;
  outcome: string;
  compilation: string;
  onsetDateTime: string | null;
  note: string;
  isDeleted: boolean;
  performerDetails: PerformerDetailsFull | null;
  createdDate: string | null;
  speciality: null;
}

// ─── Immunizations (PROD-736 PR D) ───────────────────────────────────────────

/**
 * Dose quantity sub-resource. Flutter `Quantity` class. ALL fields are
 * optional + nullable (Samer PR #253 review §4 — confirmed): the wire
 * ships the wrapper with every field literally `null` on chart-side
 * Immunization records (curl-verified 2026-05-20) so downstream
 * consumers should null-guard each field, not the wrapper itself.
 * Used by `CreateImmunizationBody.quantity` and
 * `UpdateImmunizationBody.quantity` — both ALWAYS ship the wrapper
 * (never omit), never a bare null.
 */
export interface DoseQuantity {
  value?: number | null;
  unit?: string | null;
  system?: string | null;
  code?: string | null;
}

/**
 * Single patient immunization record (FHIR `Immunization` R4 alias).
 *
 * Flutter ref: `patient_immunization_model.dart`. `vaccine` uses the
 * `Code` class — wire `display` field (NOT displayName like Procedures).
 *
 * Backend typo preserved: `administratedDate` / `administratedDisplayDate`
 * (should be "administered"). Don't "fix" — the wire only accepts the typo.
 */
export interface PatientImmunization {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  /** The vaccine itself — Flutter `Code` class, `display` field. */
  vaccine?: CodeWordCase | null;
  status?: string | null;
  /** Patient id — Immunization uses `patient` (NOT `subject` like
   *  Allergy/Condition/Procedure). */
  patient?: string | null;
  encounter?: string | null;
  performer?: string | null;
  /** ISO 8601 when the vaccine was administered. Typo preserved. */
  administratedDate?: string | null;
  administratedDisplayDate?: string | null;
  /** ISO 8601 expiry date of the vaccine lot. */
  expiryDate?: string | null;
  expiryDisplayDate?: string | null;
  lotNo?: string | null;
  /** Where on the body the vaccine was administered. */
  site?: CodeWordCase | null;
  remarks?: string | null;
  quantity?: DoseQuantity | null;
  statusReason?: string | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  performerDetails?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
  } | null;
  createdDate?: string | null;
  /** Whether this is a prescription-tracked vaccine vs a record-only entry. */
  isRx?: boolean | null;
  speciality?: CodeWordCase | null;
  /** Dose number within a vaccine series. The wire ships `Code` shapes
   *  here — `durationQuantity.code` is the dose ordinal ("1", "2", "Other",
   *  "Unknown") and `durationQuantity.display` is the human label.
   *
   *  Populated on BOTH `GetImmunizationByPatientId` (the regular list)
   *  AND `GetImmunizationTimelineByPatientId` (the chart endpoint) —
   *  same `PatientImmunization` Dart class powers both responses
   *  (`patient_chart_data_source_impl.dart:643` and `:1228`). The
   *  Immunizations chart tab doesn't render this field today, but it
   *  may surface in a future "dose number" column without a wire-shape
   *  change. */
  durationQuantity?: CodeWordCase | null;
  /** Dose unit ("Doses" / "Days" / "Weeks" / "Months"). Same `Code` shape.
   *  Same dual-endpoint availability as `durationQuantity`. */
  durationUnits?: CodeWordCase | null;
}

/**
 * Coding shape carried by Immunization wire fields (`vaccine`, `site`,
 * `durationQuantity`, `durationUnits`). Curl-verified by Andrew
 * 2026-05-20: each ships 6 fields including PascalCase `Definition`
 * (verbatim wire literal — backend reads the exact field name).
 *
 * Same shape as Social History's `historyCode` (`SocialHabitQty`) —
 * the .NET service uses a shared internal type with the PascalCase
 * `Definition` field but emits it on the wire under multiple field
 * names depending on context. Tracked: PROD-842 (wire-literal
 * cleanup — `Definition` PascalCase is one of the legacy literals).
 */
export interface ImmunizationCoding {
  code?: string | null;
  /** PascalCase wire literal — preserve verbatim. */
  Definition?: string | null;
  display?: string | null;
  system?: string | null;
  description?: string | null;
  shortName?: string | null;
}

/**
 * POST `/api/Patients/CreateImmunization` request body. Curl-verified
 * by Andrew 2026-05-20.
 *
 * **Immunization-specific wire quirks**:
 * - `administratedDate` / `administratedDisplayDate` typo preserved
 *   (should be "administered"). PROD-842 tracks. ISO no-Z + DD/MM/YYYY
 *   display partner pair.
 * - `expiryDate` / `expiryDisplayDate` same format pair.
 * - `vaccine`, `site`, `durationQuantity`, `durationUnits` all ship as
 *   `ImmunizationCoding` (6 fields including PascalCase `Definition`).
 * - `quantity` is the `DoseQuantity` wrapper — ships `{value:null,
 *   unit:null, system:null, code:null}` on Create (all-null sentinel,
 *   NOT omitted, NOT a single null).
 * - `dataAbsentReason: "n/a"` literal sentinel (PROD-842).
 * - `statusReason: "n/a"` literal sentinel (NOT null — distinct from
 *   most other chart resources where statusReason ships null).
 * - `performer` is the recording-user GUID. On Create = current user;
 *   on Update = original recorder (preserved, NOT overwritten —
 *   same pattern as Allergies' `recorder` and Procedures' `asserter`).
 * - `patient` not `subject` (Immunization is the only chart resource
 *   that uses `patient` for the subject reference).
 * - `remarks: ""` (empty string default, NOT null).
 * - `lotNo` is a string label entered by the user (free-form).
 * - `isRx` boolean — `false` for record-only entries (the usual case),
 *   `true` when the immunization came from a prescription.
 * - `encounter: null` on Create; round-tripped on Update.
 * - `createdDate: null` on Create — server stamps.
 * - `performerDetails: null` on Create — server fills the full shape.
 * - `speciality: null` (top-level — distinct from `performerDetails.
 *   speciality` which IS populated on Update round-trip).
 */
export interface CreateImmunizationBody {
  identifier: null;
  vaccine: ImmunizationCoding | null;
  status: 'Active';
  patient: string;
  encounter: string | null;
  /** Literal "n/a" sentinel — see SocialHistory dataAbsentReason for
   *  the FHIR-vs-current-backend rationale. PROD-842. */
  dataAbsentReason: 'n/a';
  /** Current user's userId on Create (recording-provider). */
  performer: string;
  expiryDisplayDate: string | null;
  administratedDisplayDate: string | null;
  /** ISO 8601 no-Z (Scrips wire convention). */
  administratedDate: string | null;
  expiryDate: string | null;
  /** All-null `DoseQuantity` sentinel on Create. The wrapper itself is
   *  always shipped (never omitted) — backend rejects missing key. */
  quantity: DoseQuantity;
  /** Literal "n/a" sentinel (NOT null — Immunization-specific). */
  statusReason: 'n/a';
  site: ImmunizationCoding | null;
  /** Empty string default — NOT null. */
  remarks: string;
  lotNo: string | null;
  isDeleted: false;
  /** Server-fills on Create — ship null. */
  performerDetails: null;
  /** Server-stamps on Create — ship null. */
  createdDate: null;
  /** `false` for record-only entries; `true` for Rx-tracked. */
  isRx: boolean;
  durationQuantity: ImmunizationCoding | null;
  durationUnits: ImmunizationCoding | null;
  /** Top-level speciality — null on Create. Performer's specialty is
   *  carried inside `performerDetails.speciality` on Update round-trip. */
  speciality: null;
}

/**
 * PUT `/api/Patients/immunization/{id}` request body. Curl-verified by
 * Andrew 2026-05-20 — both Edit and soft-Delete flows ship through
 * this endpoint, only `isDeleted` differs.
 *
 * **Round-trip rules vs CreateImmunizationBody:**
 * - `identifier` is the existing GUID.
 * - `performer` is the ORIGINAL recording user (NOT overwritten with
 *   the editing user). Same pattern as Allergies' `recorder` and
 *   Procedures' `asserter`.
 * - `performerDetails` round-trips the full shape (with speciality,
 *   timeZone, organizationId, roleName).
 * - `createdDate` preserved from original record.
 * - `encounter` round-tripped (NOT nulled).
 * - All other literals (`dataAbsentReason: "n/a"`, `statusReason:
 *   "n/a"`, `remarks: ""`, all-null `quantity`) match Create.
 * - `isDeleted: false` for Edit, `true` for soft-DELETE.
 * - Top-level `speciality: null` (same as Create).
 */
export interface UpdateImmunizationBody {
  identifier: string;
  vaccine: ImmunizationCoding | null;
  status: 'Active';
  patient: string;
  encounter: string | null;
  dataAbsentReason: 'n/a';
  /** Original recording user (NOT overwritten with editing user). */
  performer: string;
  expiryDisplayDate: string | null;
  administratedDisplayDate: string | null;
  administratedDate: string | null;
  expiryDate: string | null;
  quantity: DoseQuantity;
  statusReason: 'n/a';
  site: ImmunizationCoding | null;
  remarks: string;
  lotNo: string | null;
  /** `false` for Edit, `true` for soft-DELETE. */
  isDeleted: boolean;
  /** Full performer details from original record (preserved). */
  performerDetails: PerformerDetailsFull | null;
  /** Original creation timestamp (preserved). */
  createdDate: string | null;
  isRx: boolean;
  durationQuantity: ImmunizationCoding | null;
  durationUnits: ImmunizationCoding | null;
  speciality: null;
}

// ─── Vaccine Timeline (Vaccination Administration Record) ───────────────────

/**
 * Practice-context envelope on the vaccine-chart response. Used by the
 * Administration Record header so the printout includes the practice's
 * name, phone, and address even when rendered outside the page chrome.
 */
export interface VaccineChartPracticeResponse {
  id?: string | null;
  name?: string | null;
  phone?: string | null;
  address?: string | null;
  country?: string | null;
  city?: string | null;
  office?: string | null;
  faxNumber?: string | null;
  organizationId?: string | null;
  photo?: string | null;
}

/**
 * One vaccine category bucket in the timeline. Categories ship in a
 * defined order (`categoryOrder`) with a fixed number of dose slots
 * (`categoryNoOfDoses`). The actual patient records nested under
 * `patientImmunization` carry their own `durationQuantity.code` that
 * indexes into the dose slot ("1", "2", … "Other", "Unknown").
 *
 * Flutter ref: `vaccine_chart_model.dart` `VaccinationTimeline`.
 */
export interface VaccinationTimelineCategory {
  id?: string | null;
  categoryName?: string | null;
  /** How many doses this category expects (e.g. Hep B = 3). The Vaccine
   *  Timeline UI renders that many rows even when records are missing,
   *  showing a blank slot for each expected-but-unrecorded dose. */
  categoryNoOfDoses?: number | null;
  /** Display order — lower first. */
  categoryOrder?: number | null;
  /** Reference vaccines that belong to this category. Lightweight
   *  reference list; UI uses `patientImmunization` for the actual
   *  administered records. */
  vaccines?: Array<{ id?: string | null; code?: string | null }> | null;
  /** Patient-specific records that fall under this category. */
  patientImmunization?: PatientImmunization[] | null;
}

/** Full vaccine-chart response — practice context + per-category timelines. */
export interface VaccineChartResponse {
  practiceResponse?: VaccineChartPracticeResponse | null;
  vaccinationTimeline?: VaccinationTimelineCategory[] | null;
}

// ─── Flags (PROD-736 PR E) ───────────────────────────────────────────────────

/**
 * Flag code shape. Flutter `FlagResponse` class. Each flag carries its
 * own categoryName (e.g. "Clinical", "Behavioural", "Social").
 */
export interface FlagResponse {
  code?: string | number | null;
  display?: string | null;
  categoryName?: string | null;
}

/**
 * Single patient flag record. Flags are clinical / social / behavioural
 * banners (e.g. "Wandering risk", "Hearing impaired", "VIP donor")
 * attached to the patient record so they surface in encounters.
 *
 * Flutter ref: `patient_flags_model.dart`. Note that `startDate` /
 * `endDate` are typed `dynamic` in Flutter — wire ships ISO strings
 * sometimes and null othertimes; treat as string-or-null in TS.
 */
export interface PatientFlag {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  subject?: string | null;
  encounter?: string | null;
  author?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  /** Top-level category bucket — Flutter ships a redundant copy on top of
   *  `flagResponse.categoryName`. They should match; if they diverge we
   *  prefer the top-level value (Flutter parity). */
  categoryName?: string | null;
  /** The flag itself — `display` is the human label, `code` is the
   *  master-data identifier (sometimes a string, sometimes a number). */
  flagResponse?: FlagResponse | null;
  createdDate?: string | null;
  createdBy?: string | null;
  performerDetails?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
  } | null;
}

/**
 * Master flag-catalog row from `GET /api/Appointment/GetFlagList`
 * (curl-verified by Andrew 2026-05-20). Reused as the data source for
 * the AddFlagsDialog grid; the type already exists at
 * `@/types/scheduling-api` (`FlagListResponse`) where it serves the
 * scheduling row-warning strip — re-export here for consumers that
 * stay in the patient module to avoid cross-module import sprawl.
 *
 * Shape: each entry is one CATEGORY group with a `categoryName` and
 * an array of `FlagResponse` rows. Each `FlagResponse` carries `code`
 * (integer master id), `display` (human label), and `categoryName`
 * (echo of the parent group name — Flutter ships both and the
 * CreateFlag wire body includes both verbatim).
 */
export type { FlagListResponse } from '@/types/scheduling-api';

/**
 * Wire body for `POST /api/Patients/CreateFlag` — Add / Edit / Delete
 * (curl-verified by Andrew 2026-05-20).
 *
 * **One endpoint, whole-state replacement semantic**: the POST body is
 * an ARRAY of `PatientFlag` rows representing the patient's entire
 * flag state. The backend uses this as the new authoritative state.
 * There is NO separate `/update/{id}` or `/delete/{id}` endpoint —
 * soft-delete is just an entry with `isDeleted: true` in the array.
 *
 * **Per-row semantics** (Flutter `add_flags_dialog.dart:65-89`):
 * - **Existing active row** (selected previously, still selected):
 *   ship verbatim — `identifier`, `categoryName`, original
 *   `createdBy`, original `createdDate`, full `performerDetails`.
 *   `isDeleted: false`.
 * - **Existing active row → user UNSELECTED**:
 *   set `isDeleted: true`, preserve everything else.
 * - **Existing tombstoned row → user RE-SELECTED**:
 *   set `isDeleted: false`, preserve everything else (Flutter L73-74).
 * - **Newly added row (user selects a flag never recorded)**:
 *   `identifier: null`, `isDeleted: false`, `categoryName` from
 *   master row, `createdBy: currentUserId`, `createdDate: nowIso`,
 *   `performerDetails: {}` (empty object — Flutter `toJson` default
 *   when null; server fills the full shape on insert).
 * - **"Ghost" placeholder rows** (in the GET response with
 *   `categoryName: null` + `createdBy: null` + empty
 *   `performerDetails`): pass through VERBATIM. These are
 *   backend-allocated placeholder rows for master flags the patient
 *   has never selected; the React layer never synthesises them, only
 *   echoes whatever GET returned.
 *
 * **`dataAbsentReason: ""` literal** — Flutter `toJson` default when
 * null. Empty STRING, not the `"n/a"` sentinel that Allergies /
 * Conditions / Procedures / History use. Curl-verified. Tracked
 * separately under PROD-842 for backend consolidation.
 *
 * **`performerDetails: {}` empty object** — Flutter's `toJson`
 * defaults `performerDetails == null ? {} : ...toJson()`. Empty
 * object literal, NOT `null` and NOT `undefined`. Curl-verified.
 */
export type CreateFlagBody = PatientFlag[];

// ─── History (PROD-736 PR F) ─────────────────────────────────────────────────

/**
 * Family medical history row. Flutter `PatientMedicalHistory`. Captures a
 * relative's diagnoses + demographics so the encounter has context for
 * inherited risk.
 *
 * `code` is an ARRAY of code elements — one family member can carry
 * multiple diagnoses on a single row.
 */
export interface PatientFamilyHistory {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  /** Conditions / diagnoses for this relative. ARRAY — multiple
   *  conditions per relative on a single row. Read-side entries may
   *  carry per-row recorder / speciality / dates / description (see
   *  `FamilyHistoryCodeElement` for the full wire shape — the read
   *  type is `Partial` because not all GET responses include every
   *  optional field, and the Update path re-stamps anything missing). */
  code?: Partial<FamilyHistoryCodeElement>[] | null;
  status?: string | null;
  patient?: string | null;
  fullName?: string | null;
  /** Relation to the patient (e.g. Mother / Father / Sibling). */
  relationship?: CodeWordCase | null;
  gender?: string | null;
  age?: number | null;
  /** ISO 8601 date of birth (relative's DOB). */
  dob?: string | null;
  /** Free-text outcome ("Deceased — heart attack", "Living, well", etc.). */
  outCome?: string | null;
  note?: string | null;
  createdBy?: string | null;
  statusReason?: string | null;
  encounter?: string | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  createdDate?: string | null;
}

/**
 * Social history row. Flutter `PatientSocialHistory`. Captures
 * lifestyle observations (smoking, alcohol, exercise, occupation,
 * housing, etc.) — one row per observation type.
 *
 * `code` is a CodeCategoryData (single Coding with category info),
 * NOT an array like family history.
 */
export interface PatientSocialHistory {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  /** The observation type (e.g. Smoking, Alcohol use). */
  code?: CodeWordCase | null;
  status?: string | null;
  category?: CodeWordCase | null;
  subject?: string | null;
  /** ISO 8601 — when the observation applies. */
  effectiveDateTime?: string | null;
  /** Coded value of the observation (e.g. "Former smoker"). */
  historyCode?: CodeWordCase | null;
  /** ISO 8601 — start of the observation window. */
  fromTime?: string | null;
  fromDisplayTime?: string | null;
  /** ISO 8601 — end of the observation window. Null when still ongoing. */
  toTime?: string | null;
  toDisplayTime?: string | null;
  comment?: string | null;
  encounter?: string | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  createdBy?: string | null;
  createdDate?: string | null;
  updatedby?: string | null;
  updatedDate?: string | null;
  performerDetails?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
  } | null;
  speciality?: CodeWordCase | null;
}

/**
 * Master picker row from `GET /api/Master/GetSocialHistoryList?searchQuery=`.
 * Flutter's `CodeCategoryData` — a Coding extended with an OPTIONAL nested
 * `category` Coding. Drugs/alcohol/etc. ship with the category nested so the
 * Create wire body can carry the same nesting (the curl `code.category` is
 * populated on Create, nulled on Update — the server normalises on
 * round-trip).
 */
export interface SocialHistoryCodeRow {
  code?: string | null;
  display?: string | null;
  system?: string | null;
  category?: CodeWordCase | null;
}

/**
 * Master picker row from `GET /api/Master/GetSocialHabitQtyList`. Quantity
 * / frequency codes for the `historyCode` field (e.g. Heavy / Moderate /
 * Light / Occasional). PascalCase `Definition` preserved verbatim from the
 * wire — Andrew curl 2026-05-20 shows the field present with literal `null`
 * on both create and update payloads. Keep the casing — .NET model binder
 * is order-tolerant but case-sensitive on names.
 */
export interface SocialHabitQty {
  code?: string | null;
  /** PascalCase — backend literal, do NOT rename to `definition`. */
  Definition?: string | null;
  display?: string | null;
  system?: string | null;
  description?: string | null;
  shortName?: string | null;
}

/**
 * POST `/api/Patients/CreateSocialHistory` request body. Curl-verified
 * by Andrew 2026-05-20.
 *
 * **Social-History-specific wire quirks vs. Procedures / Conditions:**
 * - `code` ships the master picker row VERBATIM, including the nested
 *   `category` Coding (CREATE preserves the nesting; Update nulls it
 *   and keeps `category` at top-level instead — backend normalisation).
 * - Top-level `category` is duplicated from `code.category` (NOT the
 *   `category` field of the row — it's a separate top-level Coding from
 *   the master row's category descriptor).
 * - `dataAbsentReason: "n/a"` on BOTH Create and Update (unlike Procedures
 *   which ships `""` on Create).
 * - `historyCode` is a Coding extended with PascalCase `Definition` +
 *   description + shortName (see `SocialHabitQty`).
 * - `fromDisplayTime` + `toDisplayTime` are partner strings to the
 *   ISO `fromTime` / `toTime` — Flutter's `DynamicDatePicker` accepts
 *   "DD/MM/YYYY", "MM/YYYY", "YYYY", "3/2024", or duration tokens like
 *   "1Y" / "2M" / "3D". Ship both verbatim.
 * - `createdBy` set to current user; `createdDate: null` (server fills).
 * - `performerDetails: null` + `speciality: null` on Create — server fills.
 * - `comment: null` on Create when empty (Flutter ships null for empty
 *   freeform); EDIT round-trips empty string `""`.
 */
export interface CreateSocialHistoryBody {
  identifier: null;
  /** Picker row carrying the nested category. */
  code: SocialHistoryCodeRow | null;
  status: 'Active';
  /** Top-level category Coding — same as `code.category` on Create. */
  category: CodeWordCase | null;
  comment: string | null;
  subject: string;
  encounter: string | null;
  effectiveDateTime: null;
  fromDisplayTime: string | null;
  fromTime: string | null;
  toDisplayTime: string | null;
  toTime: string | null;
  historyCode: SocialHabitQty | null;
  /** Sentinel `"n/a"` STRING — NOT a FHIR CodeableConcept. FHIR R4
   *  defines `dataAbsentReason` as a coded value
   *  (`http://hl7.org/fhir/ValueSet/data-absent-reason`); the .NET
   *  service currently accepts the literal `"n/a"` string instead.
   *  Flutter ships the same literal. Tracked for cleanup:
   *  **PROD-842** — if downstream FHIR conformance (NABIDH) ever
   *  enforces ValueSet validation, this needs to migrate to the
   *  proper Coding shape. Until then, the literal is the wire
   *  contract. */
  dataAbsentReason: 'n/a';
  isDeleted: false;
  /** Current user's userId on Create. */
  createdBy: string;
  /** Server-assigned on Create — ship null. */
  createdDate: null;
  updatedby: null;
  updatedDate: null;
  performerDetails: null;
  speciality: null;
}

/**
 * PUT `/api/Patients/socialHistory/{id}` request body. Curl-verified by
 * Andrew 2026-05-20 — both EDIT and soft-DELETE flows ship through this
 * endpoint, only `isDeleted` differs.
 *
 * **Round-trip rules vs CreateSocialHistory:**
 * - `identifier` is the existing GUID.
 * - `code.category: null` on Update (backend strips the nesting after
 *   Create; the curl shows `code` as a plain `{code, display, system,
 *   category: null}` row on PUT).
 * - Top-level `category` carries the Coding instead.
 * - `createdBy`, `createdDate`, `performerDetails`, `speciality` all
 *   round-trip from the original record (NOT overwritten with the
 *   editing user).
 * - `updatedby: null` + `updatedDate: null` — server stamps both on
 *   PUT. Flutter sends them as null too (lowercase `updatedby` — the
 *   wire field is NOT camelCase, it's lower-case `updatedby`).
 * - `status: "Active" | "Inactive"` — Inactive is the "former smoker"
 *   semantic. Curl-verified the literal on both states.
 * - `dataAbsentReason: "n/a"` (same as Create).
 * - `comment: ""` instead of null on Update (Flutter `toJson` ships
 *   the empty string when the field was cleared).
 * - `isDeleted: false` for EDIT, `true` for soft-DELETE.
 */
export interface UpdateSocialHistoryBody {
  identifier: string;
  /** Plain Coding (NO nested category) on Update. */
  code: SocialHistoryCodeRow | null;
  status: 'Active' | 'Inactive';
  /** Top-level category Coding round-tripped from original record. */
  category: CodeWordCase | null;
  comment: string;
  subject: string;
  encounter: string | null;
  effectiveDateTime: null;
  fromDisplayTime: string | null;
  fromTime: string | null;
  toDisplayTime: string | null;
  toTime: string | null;
  historyCode: SocialHabitQty | null;
  /** Sentinel "n/a" — see `CreateSocialHistoryBody.dataAbsentReason`
   *  for the FHIR-vs-current-backend rationale. PROD-842. */
  dataAbsentReason: 'n/a';
  /** `false` for EDIT, `true` for soft-DELETE. */
  isDeleted: boolean;
  /** Original recording user — preserved, NOT overwritten. */
  createdBy: string | null;
  /** Original creation timestamp — preserved. */
  createdDate: string | null;
  updatedby: null;
  updatedDate: null;
  /** Full performer details from the original record. */
  performerDetails: PerformerDetailsFull | null;
  /** Top-level speciality round-tripped from original record. */
  speciality: CodeWordCase | null;
}

/**
 * One diagnosis row inside `FamilyMedicalHistory.code[]`. Each row carries
 * its own date range + recorder + speciality — Flutter `CodeElement` from
 * `patient_medical_history.dart:130`. Curl-verified 2026-05-20.
 *
 * **Per-row wire fields:**
 * - `code` / `display` / `system` — diagnosis identity (`/api/Master/
 *   DiagnosisCodes?system=ICD-10` picker result).
 * - `description` — free-text "PROBLEM DESCRIPTION" Flutter input. Null
 *   when not set.
 * - `fromTime` / `fromDisplayTime` — onset of the relative's diagnosis;
 *   Flutter `DynamicDatePicker` so accepts duration tokens too.
 * - `toTime` / `toDisplayTime` — resolution date (null when ongoing).
 * - `updatedby` — current user's userId on Create; preserved on Update.
 * - `updatedDate` — null on Create; preserved on Update.
 * - `isDeleted` — per-row tombstone (Flutter removes deleted rows from
 *   the array client-side; we follow the same pattern).
 * - `performerDetails` — null on Create; full shape on Update (the
 *   per-row recorder is what shows in the chart row).
 * - `speciality` — null on Create; per-row on Update.
 */
export interface FamilyHistoryCodeElement {
  system: string | null;
  code: string | null;
  display: string | null;
  description: string | null;
  fromTime: string | null;
  fromDisplayTime: string | null;
  toTime: string | null;
  toDisplayTime: string | null;
  isDeleted: boolean;
  updatedby: string | null;
  updatedDate: string | null;
  performerDetails: PerformerDetailsFull | null;
  speciality: CodeWordCase | null;
}

/**
 * POST `/api/Patients/CreateFamilyMedicalHistory` request body.
 * Curl-verified by Andrew 2026-05-20.
 *
 * **Family-History-specific wire quirks:**
 * - `code` is an ARRAY (multi-diagnosis per relative on a single row).
 * - Top-level demographic fields default to literals when unset:
 *   `dataAbsentReason: "n/a"`, `fullName: "n/a"`, `outCome: "n/a"`,
 *   `note: ""`. These are Flutter `toJson` defaults — match verbatim
 *   so backend validators don't reject missing fields.
 * - `gender: null`, `age: 0`, `dob: null` — Flutter ships `age: 0` as
 *   the "unset" sentinel (not null) when the form field is blank.
 * - `outCome` is the typo wire literal (NOT `outcome`) — same root
 *   cause as Procedures' `compilation` typo. **Tracked for cleanup:
 *   PROD-842** (backend wire-literal cleanup ticket, filed 2026-05-20).
 * - `dataAbsentReason: "n/a"` is a SENTINEL STRING, not a FHIR
 *   CodeableConcept. FHIR R4 spec defines `dataAbsentReason` as a
 *   coded value (`http://hl7.org/fhir/ValueSet/data-absent-reason`);
 *   the .NET service currently accepts the literal `"n/a"` string
 *   instead. Also tracked under PROD-842 for downstream FHIR
 *   conformance — if NABIDH ever enforces ValueSet validation we'll
 *   need to ship the proper Coding shape.
 * - `relationship.system: "NAB041"` — the FamilyHistory category in
 *   the NAB taxonomy. Curl-verified.
 * - `createdBy` set to current user; `createdDate: null` (server fills).
 * - `encounter: null` on Create; round-tripped on Update.
 * - `status: "Active"` (Flutter `toJson` default when null).
 * - `statusReason: null`.
 */
export interface CreateFamilyMedicalHistoryBody {
  identifier: null;
  code: FamilyHistoryCodeElement[];
  status: 'Active';
  patient: string;
  /** Literal "n/a" sentinel — Flutter default when null. NOT a FHIR
   *  CodeableConcept (PROD-842 tracks the cleanup). */
  dataAbsentReason: 'n/a';
  /** Literal "n/a" — Flutter default when null. UI never shows this. */
  fullName: 'n/a';
  relationship: CodeWordCase | null;
  gender: null;
  /** Flutter ships `0` as the "unset" sentinel (NOT null). */
  age: number;
  dob: null;
  /** Backend typo literal `outCome` (capital C — should be `outcome`).
   *  Preserve verbatim — Flutter PM/PA still ships the typo and the
   *  .NET service reads the literal field name. Tracked: PROD-842. */
  outCome: 'n/a';
  /** Empty string on Create — Flutter default when null. */
  note: string;
  statusReason: null;
  encounter: string | null;
  isDeleted: false;
  /** Server-assigned on Create — ship null. */
  createdDate: null;
  /** Current user's userId on Create. */
  createdBy: string;
}

/**
 * PUT `/api/Patients/familyMedicalHistory/{id}` request body.
 * Curl-verified by Andrew 2026-05-20.
 *
 * **Round-trip rules vs CreateFamilyMedicalHistory:**
 * - `identifier` is the existing GUID.
 * - `code[]` entries preserve their per-row `performerDetails`,
 *   `speciality`, `updatedby`, `updatedDate` — Edit refreshes the
 *   `updatedby` / `updatedDate` ONLY on rows the user actually modified
 *   (we match Flutter: ship the original values for unchanged rows,
 *   stamp current user for new rows).
 * - `createdDate` + `createdBy` preserved from original record.
 * - `encounter` round-tripped (NOT nulled).
 * - `status: "Active"` (only Active is observed on the wire for family
 *   history; no inactive state in the Flutter UI).
 * - `outCome: "n/a"` literal preserved (no UI to change it).
 * - `note: ""` round-tripped from original (Flutter `toJson` defaults
 *   null → "").
 * - `gender: null` (no UI to change).
 * - `age: 0` (no UI to change).
 * - `isDeleted: false` for EDIT, `true` for soft-DELETE.
 */
export interface UpdateFamilyMedicalHistoryBody {
  identifier: string;
  code: FamilyHistoryCodeElement[];
  status: 'Active';
  patient: string;
  /** Sentinel "n/a" — see `CreateFamilyMedicalHistoryBody.dataAbsentReason`
   *  for the FHIR-vs-current-backend rationale. PROD-842. */
  dataAbsentReason: 'n/a';
  fullName: 'n/a';
  relationship: CodeWordCase | null;
  gender: null;
  age: number;
  dob: null;
  /** Backend typo literal — see CreateFamilyMedicalHistoryBody. PROD-842. */
  outCome: 'n/a';
  note: string;
  statusReason: null;
  encounter: string | null;
  /** `false` for EDIT, `true` for soft-DELETE. */
  isDeleted: boolean;
  /** Original creation timestamp — preserved. */
  createdDate: string | null;
  /** Original recording user — preserved. */
  createdBy: string | null;
}

// ─── Medications (PROD-737 PR A) ─────────────────────────────────────────────

/**
 * Active / Past status keys for `/GetPatientMedicationDetails?IsActive=`.
 * Boolean on the wire — `true` returns active meds, `false` returns past
 * (completed / stopped / cancelled).
 */
export const MEDICATION_STATUS = {
  active: true,
  past: false,
} as const;

/** Administration sub-resource — when/how the medication was given. */
export interface MedicationAdministration {
  startDate?: string | null;
  startDisplayDate?: string | null;
  endDate?: string | null;
  endDisplayDate?: string | null;
  note?: string | null;
  status?: string | null;
  performer?: string | null;
  performerDetails?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
  } | null;
}

/** One dose-occurrence row — Flutter `OccurenceRequest`. */
export interface MedicationOccurrence {
  /** Units per intake (e.g. "1.5"). */
  count?: string | null;
  /** Times per day. */
  period?: number | null;
}

/**
 * Single patient medication record (FHIR `MedicationStatement` /
 * `MedicationRequest` R4 alias depending on `isOrder`).
 *
 * `isOrder=true` → prescribed medication (Rx).
 * `isOrder=false` → administered / recorded medication only.
 *
 * Flutter ref: `patient_medications.dart`. `code` is the medication
 * itself (Flutter `Code` class — wire `display` field).
 */
export interface PatientMedication {
  /** Server-assigned GUID. Absent / omitted on create. */
  identifier?: string | null;
  /** The medication itself — Flutter `Code` class, `display` field. */
  code?: (CodeWordCase & { /** Readable name ("Crestor 10 mg"), Scrips.Patient #1224. Absent on older servers. */ shortDisplay?: string | null }) | null;
  status?: string | null;
  manufacturer?: string | null;
  /** Form — pill / liquid / cream / etc. */
  form?: CodeWordCase | null;
  /** Route — oral / IV / topical / etc. */
  route?: CodeWordCase | null;
  unitOfMeasurement?: string | null;
  /** Frequency (wire ships as `frequency`, Flutter renames to `freq`). */
  frequency?: string | null;
  duration?: string | null;
  durationType?: string | null;
  encounter?: string | null;
  orderId?: string | null;
  /** True when this is an Rx (prescription); false when administered-only. */
  isOrder?: boolean | null;
  administration?: MedicationAdministration | null;
  occurenceRequest?: MedicationOccurrence[] | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  createdDate?: string | null;
  speciality?: CodeWordCase | null;
  /** The prescription order (PatientRxOrder.Id) this row reconciles against; absent when it is not an Rx (Scrips.Patient MedicationService). */
  previousRxId?: string | null;
  /** PROD-1653: the latest reconciliation of the order: Taking | NotTaking | Unverified, server-stamped. No encounter id or note on the read. */
  reconciliationStatus?: string | null;
  reconciliationRecordedBy?: string | null;
  reconciliationRecordedAt?: string | null;
}

// ─── Drug Interactions (PROD-737 PR B) ───────────────────────────────────────

/**
 * Drug interaction alert record. Flutter `PatientDrugInteraction`.
 *
 * Wire quirks to preserve:
 *   - Backend typo `medicationname` (one word, lowercase). DO NOT
 *     "fix" to `medicationName` — the wire only accepts the typo.
 *   - `isDeleted` is typed `dynamic` in Flutter — backend may ship
 *     `null` / `false` / `true` / `"false"` interchangeably. Filter
 *     defensively with strict `!== true`.
 *
 * Severity values observed on dev: "Severe" / "High" / "Moderate" /
 * "Mild" / "Low" / "Unknown".
 *
 * `alertType` enumerates the interaction category — "DrugDrug",
 * "DrugAllergy", "DrugDisease", "DrugFood", etc.
 */
export interface PatientDrugInteraction {
  id?: string | null;
  /** The active medication this alert is attached to. */
  patientRxId?: string | null;
  /** Backend typo — lowercase one-word. Don't normalise. */
  medicationname?: string | null;
  title?: string | null;
  /** Category — DrugDrug, DrugAllergy, DrugDisease, DrugFood, etc. */
  alertType?: string | null;
  description?: string | null;
  severity?: string | null;
  subType?: string | null;
  /** The OTHER medication / allergen / condition that triggered the
   *  interaction. */
  triggeredBy?: string | null;
  triggeredType?: string | null;
  triggeredDetail?: string | null;
  isActive?: boolean | null;
  /** Loose-typed on the wire — see doc-block. Filter with strict
   *  `!== true`. */
  isDeleted?: boolean | string | null;
}

// ─── Labs (PROD-738 PR A) ────────────────────────────────────────────────────

/**
 * One radiology / lab order entry nested inside a `PatientLab`. Flutter
 * `AssessmentRadiology`. The wire name is `radiologyOrderRequest` even
 * though it covers BOTH radiology and laboratory orders — sloppy
 * backend naming, preserved verbatim.
 *
 * `code` uses `displayName` (NOT `display` — same divergence as
 * Procedures, see `CodeDisplayName`).
 */
export interface LabOrderEntry {
  id?: string | null;
  status?: string | null;
  intent?: string | null;
  categoryCode?: string | null;
  /** "Laboratory" / "Radiology" / etc. */
  categoryName?: string | null;
  priority?: string | null;
  /** The test itself. Uses `displayName` per `CodeDisplayName`. */
  code?: CodeDisplayName | null;
  note?: string | null;
  /** Plain-text note (the rich `note` may carry markup). The chart Note
   *  column renders THIS (2026-06-09 wire-confirmed on the order entry). */
  notePlainText?: string | null;
  isDeleted?: boolean | null;
}

/**
 * One patient lab record. Flutter `PatientLab`. The wire wraps each
 * row's actual order entries in `radiologyOrderRequest` (the naming
 * covers both radiology AND lab orders — Flutter's title getter pulls
 * `radiologyOrderRequest.first.code.displayName`).
 *
 * In the React view we flatten one table row per nested order entry
 * so multi-test orders show every test, not just the first.
 */
export interface PatientLab {
  /** Server-assigned GUID. */
  identifier?: string | null;
  encounter?: string | null;
  subject?: string | null;
  performer?: string | null;
  notes?: string | null;
  /** Plain-text order-level note — the Note column renders this (the rich
   *  `notes` may carry markup). 2026-06-09 wire-confirmed. */
  notePlainText?: string | null;
  /** ISO 8601 — when this lab record was created. */
  createdDate?: string | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  /** Nested order entries — one per requested test. Sloppy backend
   *  naming: covers BOTH radiology and laboratory orders. */
  radiologyOrderRequest?: LabOrderEntry[] | null;
  performerDetails?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
  } | null;
}

// ─── Documents (PROD-738 PR B) ───────────────────────────────────────────────

/**
 * Request body for `POST /api/Patients/GetPatientDocuments`. Server-side
 * pagination + filters. Mirrors Flutter `PatientDocumentRequestParam`.
 */
export interface PatientDocumentsRequest {
  patientId?: string | null;
  encounterId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Filter by file extension family (e.g. ["PDF","JPG"]). */
  fileType?: string[] | null;
  /** Filter by uploader id. */
  uploadedBy?: string[] | null;
  pageNumber?: number;
  pageSize?: number;
  isArchived?: boolean;
}

/**
 * One file attached to a `PatientDocument`. A document can carry
 * multiple files (e.g. front + back of a referral letter).
 */
export interface PatientDocumentFile {
  id?: string | null;
  patientDocumentId?: string | null;
  url?: string | null;
  fileName?: string | null;
  fileType?: string | null;
  /** Loose-typed on the wire (null/false default). Filter with
   *  strict `!== true`. */
  isDeleted?: boolean | null;
}

/**
 * Single patient document record. Flutter `PatientDocumentModel`.
 *
 * Each document is a logical "envelope" (Lab report / Referral letter /
 * Discharge summary / etc.) that wraps one or more file attachments.
 * The `type` field carries the envelope category; individual files
 * carry their own `fileType` (PDF / JPG / etc.).
 *
 * `reviewedById` / `reviewedOn` track per-document clinical review —
 * a doctor confirms they've seen the document. Distinct from
 * `createdBy` (uploader).
 */
export interface PatientDocument {
  id?: string | null;
  patientId?: string | null;
  encounterId?: string | null;
  /** Document title (human-readable). */
  name?: string | null;
  /** Category — "Lab report" / "Referral" / "Discharge" / etc. */
  type?: string | null;
  /** ISO 8601 — when the document was issued (not when uploaded). */
  documentDate?: string | null;
  doctorName?: string | null;
  facilityName?: string | null;
  /** Free-text comma-separated tags. */
  tags?: string | null;
  reviewedById?: string | null;
  reviewedByName?: string | null;
  reviewedOn?: string | null;
  createdById?: string | null;
  createdByName?: string | null;
  /** ISO 8601 — when uploaded. */
  createdOn?: string | null;
  updatedById?: string | null;
  updatedByName?: string | null;
  updatedOn?: string | null;
  /** Tombstone — required (Samer PR #157 pattern). */
  isDeleted: boolean;
  /** File attachments — at least one expected, but the wire can ship
   *  an empty array for placeholder records. */
  patientDocumentFiles?: PatientDocumentFile[];
  /**
   * CPT / radiology code, `{ title, code }` (PROD-1769).
   *
   * This type was narrowed for a read-only table and then reused as the SOURCE
   * for a full-body write. `data` and `patientDocumentLabs` were missing here, so
   * `patients/lib/patient-document-adapter.ts` filled them with literals —
   * `data: null` and `patientDocumentLabs: []` — and the archive /
   * mark-reviewed mutations re-POSTed that stripped body to the same upsert
   * endpoint. Archiving a Procedure or Radiology document from the chart tab
   * therefore blanked its stored code.
   *
   * The values were never absent at runtime: the SAME endpoint
   * (`POST /api/Patients/GetPatientDocuments`) is typed `PatientDocumentModel` by
   * `documents/services/use-patient-documents.ts`, which does declare both.
   * Nothing prunes them — this was a TS-only omission that the adapter papered
   * over with literals instead of widening.
   */
  data?: PatientDocumentData | null;
  /** Lab result rows (PROD-1769) — see `data` above for the same history. */
  patientDocumentLabs?: PatientDocumentLab[];
}

// ─── Notes (PROD-738 PR C) ───────────────────────────────────────────────────

/**
 * One encounter row from the Notes tab. Flutter `EncounterModel` —
 * the same model the encounter shell uses — projected here as a slim
 * read shape for the chart Notes list.
 *
 * The "notes tab" name is a Flutter UX label; the wire returns
 * **encounters**, not freestanding note records. Each encounter card
 * is a chronological visit; the actual SOAP body lives on the
 * encounter detail screen (out of scope for the patient chart — that
 * surface ships under the encounter epic).
 *
 * Wire wraps the array in `{ patientEncounterList: [...] }` — unwrap
 * at the hook boundary.
 */
export interface PatientEncounterNote {
  /** Encounter id (server-assigned GUID). */
  identifier?: string | null;
  /** "in-progress" / "finished" / "cancelled" / etc. */
  status?: string | null;
  /** "Office Visit" / "Telemed" / etc. */
  type?: string | null;
  serviceType?: string | null;
  /** ISO 8601 — when the encounter started. */
  startDate?: string | null;
  /** ISO 8601 — when the encounter ended (null while in progress). */
  endDate?: string | null;
  /** ISO 8601 — record creation. */
  createdDate?: string | null;
  /** Duration in minutes (numeric on wire). */
  duration?: number | null;
  /** Chief complaint / RFV code id. */
  reasonForVisit?: string | null;
  /** Chief complaint display name — what the row title actually shows. */
  reasonForVisitName?: string | null;
  /** Location id + pre-resolved display. */
  location?: string | null;
  locationName?: string | null;
  /** Tombstone flag — wire ships null/false/true; filter strict !== true. */
  isDeleted?: boolean | null;
  /** The doctor who held the encounter. */
  performerDetails?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
  } | null;
  /** Encounter specialty (CodeList shape — pre-resolved). */
  speciality?: CodeWordCase | null;
}
